import React, { useContext, useEffect, useState } from "react";
import { UserContext } from "../../CheckoutPage";
import { useDesignVersion } from "../../../../Utils/DesignVersionConfig";
import ReactGA from "react-ga4";
import { trackData } from "../../../../Utils/attribution/CheckoutAttribution";
import { useToast } from "../../../../contexts/ToastContext";
import {
  FinancialData,
  ItemDetailsData,
  RecommendedItem,
} from "../../../../Utils/DefinitionsInterfaces";
import {
  doNFOTransactionResponse,
  getSchoolByCode,
} from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";
import V2StudentDetails from "./V2StudentDetails";
import V2DeliveryAddress from "./V2DeliveryAddress";
// import V2PaymentMethod from "./V2PaymentMethod";
import V2PurchaseDetails from "./V2PurchaseDetails";
import UniversalCheckoutHeader from "../../../../UniversalComponents/CheckoutHeader/CheckoutHeader";
import CheckoutFooter from "../CheckoutFooter/CheckoutFooter";
import CheckoutPaymentFooter from "../CheckoutPaymentFooter/CheckoutPaymentFooter";
import V2SchoolErrorMessage from "./V2SchoolErrorMessage";
import SubtotalHeader from "../SubtotalHeader/SubtotalHeader";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import "./V2CheckoutLayout.css";

interface V2CheckoutLayoutProps {
  stateDefaultItems: FinancialData[];
  stateCheckoutItems: ItemDetailsData[];
  stateRecommendedItems: RecommendedItem[];
  cbRemoveProduct: (itemId: number) => void;
  cbAddRecommended: (itemId: number) => void;
  cbHandlePaymentClick: (data?: doNFOTransactionResponse) => void;
}

const V2CheckoutLayout: React.FC<V2CheckoutLayoutProps> = (props) => {
  const { config } = useDesignVersion();

  // Check if we should hide address forms (rollNumber + autoApplyRollAsCoupon)
  const shouldHideAddressForms = () => {
    const rollNumber = sessionStorage.getItem("rollNumber");
    const hasAutoApplyCoupon = props.stateDefaultItems.some(
      (item) => item.autoApplyRollAsCoupon === true
    );
    // rollNumber && hasAutoApplyCoupon;
    return true;
  };
  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isValidatingAccess, setIsValidatingAccess] = useState(false);
  const [accessError, setAccessError] = useState<string>("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    "card" | "cod"
  >("card");

  // Get URL parameters for school-based registration
  const urlParams = new URLSearchParams(window.location.search);
  const schoolCode = urlParams.get("scl");
  // Returning-student physical certificate offer (passed from the certificate
  // portal banner). When true we collect a shipping address for the free
  // printed certificate.
  const wantsPhysicalCertificate =
    urlParams.get("add_physical_certificate") === "true";

  const userDetails = userContext?.userDetails ?? {
    name: "",
    email: "",
    school: "",
    schoolID: 0,
    grade: 0,
    mobileNumber: "",
    address: "",
    flatNo: "",
    city: "",
    state: "",
    pincode: "",
  };
  const backendSchool = userContext?.backendSchool ?? {
    schoolID: 0,
    schoolName: "",
    linkActive: false,
    city: "",
    state: "",
    examDate: "",
    isInternational: false,
    version_no: "V1",
    pg: "razorpay",
  };
  const individualReg = userContext?.individualReg ?? false;
  const setBackendSchool = userContext?.setBackendSchool ?? (() => {});
  const setIndividualReg = userContext?.setIndividualReg ?? (() => {});

  const handlePaymentMethodChange = (method: "card" | "cod") => {
    console.log("V2CheckoutLayout - Payment method changed:", {
      from: selectedPaymentMethod,
      to: method,
      timestamp: new Date().toISOString(),
    });

    setSelectedPaymentMethod(method);
    // Store in session storage for persistence
    sessionStorage.setItem("selectedPaymentMethod", method);

    console.log("V2CheckoutLayout - Session storage updated:", {
      key: "selectedPaymentMethod",
      value: method,
      sessionStorageValue: sessionStorage.getItem("selectedPaymentMethod"),
    });
  };

  // Track form completion for analytics
  const trackFormCompletion = () => {
    const requiredFields = {
      name: userDetails.name,
      mobileNumber: userDetails.mobileNumber,
      pincode: userDetails.pincode,
    };

    const isFormComplete = Object.values(requiredFields).every(
      (field) => field !== ""
    );

    if (isFormComplete) {
      trackData("5678_checkout_fields_filled");
      ReactGA.event({
        category: "5678_checkout_fields_filled",
        action: "5678_checkout_fields_filled",
        label: "5678_checkout_fields_filled",
      });
    }
  };

  // Load payment method from session storage on mount
  useEffect(() => {
    const savedMethod = sessionStorage.getItem("selectedPaymentMethod") as
      | "card"
      | "cod";
    console.log(
      "V2CheckoutLayout - Loading payment method from session storage:",
      {
        savedMethod,
        currentSelectedMethod: selectedPaymentMethod,
        timestamp: new Date().toISOString(),
      }
    );

    if (savedMethod) {
      setSelectedPaymentMethod(savedMethod);
      console.log(
        "V2CheckoutLayout - Payment method restored from session storage:",
        savedMethod
      );
    } else {
      console.log(
        "V2CheckoutLayout - No saved payment method found, using default: card"
      );
    }
  }, []);

  // School link validation for non-individual registrations
  useEffect(() => {
    // Only validate if we have a school code and it's not individual registration
    if (schoolCode && !individualReg && backendSchool.schoolID === 0) {
      setIsValidatingAccess(true);
      getSchoolByCode(schoolCode)
        .then((schoolDetails) => {
          if (schoolDetails && schoolDetails.schoolID !== 0) {
            // Check if school registration is active
            if (!schoolDetails.linkActive) {
              navigate(`/LinkUnavailable/?&scl=${schoolCode}`);
              return;
            }

            // Set school details in context
            setBackendSchool(schoolDetails);
            setIndividualReg(schoolDetails.individual_registration);

            // Store in session storage
            sessionStorage.setItem(
              "school",
              `${schoolDetails.schoolName}, ${schoolDetails.city}`
            );
            sessionStorage.setItem(
              "schoolID",
              JSON.stringify(schoolDetails.schoolID)
            );
            sessionStorage.setItem(
              "individual_registration",
              JSON.stringify(schoolDetails.individual_registration)
            );
            sessionStorage.setItem("examDate", schoolDetails.examDate);
            sessionStorage.setItem("pg", schoolDetails.pg);
            sessionStorage.setItem(
              "isInternationalTransaction",
              JSON.stringify(schoolDetails.isInternational)
            );

            // Initialize payment gateway configuration
            if (schoolDetails.isInternational) {
              console.log(
                "International school detected - configuring international payment flow"
              );
            } else {
              console.log(
                "Domestic school detected - configuring domestic payment flow"
              );
            }

            setIsValidatingAccess(false);
          } else {
            setAccessError("invalid_school_code");
            setIsValidatingAccess(false);
          }
        })
        .catch((error) => {
          console.error("Error validating school access:", error);
          showToast(
            "Failed to validate school access. Please try again.",
            "error"
          );
          setAccessError("validation_failed");
          setIsValidatingAccess(false);
        });
    }
  }, [
    schoolCode,
    individualReg,
    backendSchool.schoolID,
    navigate,
    setBackendSchool,
    setIndividualReg,
  ]);

  // Monitor form completion for analytics
  useEffect(() => {
    trackFormCompletion();
  }, [userDetails]);

  // Track pageview for V2 design
  useEffect(() => {
    trackData("5678_checkout_pageview");
    ReactGA.event({
      category: "5678_checkout_pageview",
      action: "5678_checkout_pageview",
      label: "5678_checkout_pageview",
    });
  }, []);

  if (config.version !== "v2") {
    return null;
  }

  // Show loading during access validation
  if (isValidatingAccess) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <CircularProgress />
        <div
          style={{
            color: "#666",
            fontSize: "16px",
            fontFamily: "Cardo, Helvetica",
          }}
        >
          Validating school access...
        </div>
      </div>
    );
  }

  // Show error if access validation failed
  if (accessError) {
    const getErrorMessage = () => {
      switch (accessError) {
        case "invalid_school_code":
          return "Invalid school code. Please verify the link you received from your school.";
        case "validation_failed":
          return "Unable to validate school access. Please check your internet connection and try again.";
        default:
          return "An unexpected error occurred. Please try again later.";
      }
    };

    return (
      <>
        <UniversalCheckoutHeader />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            padding: "40px 20px",
          }}
        >
          <V2SchoolErrorMessage
            isIndividualReg={individualReg}
            schoolName={backendSchool.schoolName}
            errorType="access"
            customMessage={getErrorMessage()}
          />
        </div>
        <CheckoutFooter />
      </>
    );
  }

  return (
    <>
      <SubtotalHeader />
      <UniversalCheckoutHeader />

      <div className="v2-checkout-layout">
        <div className="v2-main-content">
          <div className="v2-left-section">
            <div className="v2-form-sections">
              <V2StudentDetails />
              {wantsPhysicalCertificate && <V2DeliveryAddress />}
              {/* <V2PaymentMethod
                cbHandlePaymentClick={props.cbHandlePaymentClick}
                onPaymentMethodChange={handlePaymentMethodChange}
                selectedPaymentMethod={selectedPaymentMethod}
                sectionNumber={shouldHideAddressForms() ? 2 : 3}
                stateDefaultItems={props.stateDefaultItems}
              /> */}
            </div>
          </div>

          <div className="v2-right-section">
            <V2PurchaseDetails
              stateDefaultItems={props.stateDefaultItems}
              stateCheckoutItems={props.stateCheckoutItems}
              stateRecommendedItems={props.stateRecommendedItems}
              cbRemoveProduct={props.cbRemoveProduct}
              cbAddRecommended={props.cbAddRecommended}
              cbHandlePaymentClick={props.cbHandlePaymentClick}
            />
          </div>
        </div>
      </div>
      <CheckoutPaymentFooter />
      <CheckoutFooter />
    </>
  );
};

export default V2CheckoutLayout;
